import Cookies from 'universal-cookie';
import { Oval } from 'react-loader-spinner';
import { countries } from 'country-data';
import APIServices from '../services/APIServices';
import appToast, { TOAST_TYPE } from './AppToast';
import { ProfileCompletionStrings } from '../pages/auth/strings/authStrings';
import moment from 'moment-timezone';
import { io } from 'socket.io-client'; // Import socket.io-client
import boardToast from 'pages/projects/kanban/utils/boardToast';
import React from 'react';

export const PAGE_LIMIT = 14;

export const ACTIVITY_PAGE_LIMIT = 20;

const cookies = new Cookies();

export const userType = 'owner';

export const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB in bytes

export const userNameLimit = 40;

export const scopeTitleLimit = 60;

export const orgNameLimit = 30;

export const emailLimit = 80;

export const stateLimit = 40;

export const priceLimit = 12;

export const titleLimit = 80;

export const templateTitleLimit = 30;

export const lineItemLimit = 80;

export const descriptionLimit = 300;

export const getCookies = (name) => {
  return cookies.get(name);
};

export const setCookies = (name, value, expiryAfterMinutes = 86400) => {
  let expire = new Date();
  //Application somehow unable to set cookie so storing token in sessionStorage and reading from same
  //sessionStorage.setItem(name,value)
  expire.setDate(expire.getDate() + expiryAfterMinutes);
  try {
    if (name === 'authToken') cookies.set(name, value, { expires: expire, path: '/', secure: true });
    else cookies.set(name, value, { expires: expire, path: '/' });
  } catch (err) {
    console.log(`Error setting cookie ${name} - value is ${value} --> ${err}`);
  }
};

// To fetch the user details from the api.
export const getUserDetails = async (token) => {
  let userRes = await APIServices.get(`/user/detail?token=${token}`);

  if (userRes.data) {
    return userRes.data;
  }

  return {};
};

export const getSocketConnected = (userId = '') => {
  const newSocket = io(process.env.REACT_APP_BASE_URL, {
    query: { userId }
  });
  return newSocket;
};

//signup form default values
export const formFields = {
  isTotorialVisited: true,
  fullName: '',
  email: '',
  phoneNumber: '',
  countryDialCode: '+1',
  companyName: '',
  countryCode: 'US',
  profilePic: null
};

// Validate text input fields
export const validateTextInput = (value) => {
  const trimmedValue = value?.trim();
  const isValid = trimmedValue && trimmedValue.length >= 2 && /[a-zA-Z]/.test(trimmedValue);

  return {
    isValid,
    cleanedValue: trimmedValue,
    message: isValid
      ? 'Valid input'
      : // : startsWithInvalidChar
        // ? "Field should not start with a number or special character."
        !(trimmedValue?.length >= 2)
        ? 'Please enter at least 2 characters.'
        : 'Please ensure the field contains alphabets.'
  };
};

//function to check and validate the password strength
export const checkPasswordStrength = (password) => {
  let strengthLevel = 0;

  // Check for minimum length (e.g., at least 8 characters)
  if (password.length < 8) {
    return {
      strengthLevel: 0,
      error: 'Password must be at least 8 characters long.'
    };
  }

  strengthLevel++;

  // Check for at least one uppercase letter
  if (!/[A-Z]/.test(password)) {
    return {
      strengthLevel: 1,
      error: 'Password must contain at least one uppercase letter.'
    };
  }

  strengthLevel++;

  // Check for at least one number
  if (!/[0-9]/.test(password)) {
    return {
      strengthLevel: 2,
      error: 'Password must contain at least one number.'
    };
  }

  strengthLevel++;

  // Check for at least one special character
  if (!/[^A-Za-z0-9]/.test(password)) {
    return {
      strengthLevel: 3,
      error: 'Password must contain at least one special character.'
    };
  }

  strengthLevel++; // All criteria met

  // Return the strength level and no error
  return {
    strengthLevel,
    error: ''
  };
};

//function to validate the form for registration page
export const validateForm = (formValues, validateCompany = false) => {
  let tempErrors = {};
  if (!validateTextInput(formValues.fullName).isValid) {
    if (!validateTextInput(formValues.fullName).cleanedValue) {
      tempErrors.fullName = 'Full Name is required.';
    } else if (/^[^a-zA-Z]/.test(validateTextInput(formValues.fullName).cleanedValue)) {
      tempErrors.fullName = 'Field should not start with a number or special character.';
    } else {
      tempErrors.fullName = validateTextInput(formValues.fullName).message;
    }
  }
  if (!validateEmail(formValues.email)) {
    tempErrors.email = 'Email Address is required.';
  }
  if (!validateEmail(formValues.email) && formValues.email?.trim().length > 0) {
    tempErrors.email = 'Please enter a valid email address.';
  }
  if (!validateTextInput(formValues.companyName).isValid && validateCompany) {
    if (!validateTextInput(formValues.companyName).cleanedValue) {
      tempErrors.companyName = 'Company name is required.';
    } else {
      tempErrors.companyName = validateTextInput(formValues.companyName).message;
    }
  }
  if (!getPlainPhoneNumber(formValues.phoneNumber)?.trim()) {
    tempErrors.phoneNumber = 'Phone number is required.';
  } else if (getPlainPhoneNumber(formValues.phoneNumber).length < 10)
    tempErrors.phoneNumber = 'PhoneNumber should be 10-15 digits long.';
  return tempErrors;
};

// Common loader for the button loading.
export const selectLoader = (size = 20, padding = '10px') => (
  <div
    className="loader-wrapper"
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: padding
    }}>
    <Oval height={size} width={size} color="#ffff" ariaLabel="oval-loading" wrapperStyle={{}} wrapperClass="" />
  </div>
);

// Countries options and dialcode data list for phone number input
export const countryOptions = countries.all
  .filter((country) => country.countryCallingCodes.length > 0)
  .map((country) => ({
    value: country.alpha2,
    label: `${country.name} (${country.countryCallingCodes[0]})`,
    dialCode: country.countryCallingCodes[0],
    flag: `flag-icon flag-icon-${country.alpha2.toLowerCase()}`,
    name: country.name
  }));

//Validate the email
export const validateEmail = (email, stateUpdate) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]{2,})*$/;
  const consecutiveDotsRegex = /\.\./;
  const trimmedEmail = email?.trim();
  const schemeRegex = /^[a-zA-Z]+:/;

  const isValid =
    emailRegex.test(trimmedEmail) && !consecutiveDotsRegex.test(trimmedEmail) && !schemeRegex.test(trimmedEmail);

  if (!isValid) {
    if (stateUpdate) {
      if (!trimmedEmail.trim()) {
        stateUpdate('Email Address is required.');
      } else {
        stateUpdate('Please enter a valid email address.');
      }
    }
    return false;
  }
  return true;
};

//validate phone number for login page
export const validatePhoneNumber = (phoneNumber, setPhoneErrors) => {
  const isValid = phoneNumber && phoneNumber.length >= 10;
  if (!isValid) {
    if (setPhoneErrors) {
      if (phoneNumber.length === 0) {
        setPhoneErrors('Phone Number is required.');
      } else if (!(phoneNumber.length >= 10 && phoneNumber.length <= 15)) {
        setPhoneErrors('Phone Number should be 10-15 digits long.');
      }
    }
  }
  return isValid;
};

//manages the remember me functionalities on the login page
export const manageRememberMe = (verificationType, email, phoneDetails, rememberDevice, country) => {
  if (verificationType === 'email') {
    cookies.set('userEmail', email, { path: '/', maxAge: 7 * 24 * 60 * 60 });
  } else if (verificationType === 'phone') {
    cookies.set('userPhone', phoneDetails, {
      path: '/',
      maxAge: 7 * 24 * 60 * 60
    });
    cookies.set('country', country, {
      path: '/',
      maxAge: 7 * 24 * 60 * 60
    });
  }
  if (rememberDevice) {
    cookies.set('verificationType', verificationType, {
      path: '/',
      maxAge: 7 * 24 * 60 * 60
    });

    if (verificationType === 'email') {
      cookies.set('email', email, { path: '/', maxAge: 7 * 24 * 60 * 60 });
      cookies.remove('phoneDetails'); // Remove phone details if previously stored
      cookies.remove('country');
    } else if (verificationType === 'phone') {
      cookies.set('phoneDetails', phoneDetails, {
        path: '/',
        maxAge: 7 * 24 * 60 * 60
      });
      cookies.set('country', country, {
        path: '/',
        maxAge: 7 * 24 * 60 * 60
      });
      cookies.remove('email'); // Remove email if previously stored
    }

    cookies.set('rememberDevice', rememberDevice, {
      path: '/',
      maxAge: 7 * 24 * 60 * 60
    });
  } else {
    // Clear all related cookies if "Remember this device" is not selected
    cookies.remove('email');
    cookies.remove('phoneDetails');
    cookies.remove('verificationType');
    cookies.remove('rememberDevice');
    cookies.remove('country');
  }
  cookies.set('userType', verificationType, {
    path: '/',
    maxAge: 7 * 24 * 60 * 60
  });
};

// Function to get the plain numeric phone number for API
export const getPlainPhoneNumber = (phoneNumber) => {
  let plainNumber = '';
  // Remove formatting from the phone number
  plainNumber = phoneNumber?.replace(/[^0-9]/g, '');
  return plainNumber;
};

export const getformattedPhoneNumber = (value) => {
  const numericInput = value?.replace(/[^0-9]/g, '').slice(0, 14); // Limit to 14 digits

  // Format the phone number as (XXX) XXX-XXXX
  let formattedNumber = numericInput;

  if (numericInput?.length > 3 && numericInput?.length <= 6) {
    // Format as (XXX) XXX
    formattedNumber = `(${numericInput.slice(0, 3)}) ${numericInput.slice(3)}`;
  } else if (numericInput?.length > 6) {
    // Format as (XXX) XXX-XXXX
    formattedNumber = `(${numericInput.slice(0, 3)}) ${numericInput.slice(3, 6)}-${numericInput.slice(6, 14)}`;
  }

  return formattedNumber || '';
};

//Homapege tutorials steps
export const HomeSteps = [
  {
    target: '.search-box',
    title: 'Find What You Need with Search',
    content: 'Quickly locate projects, tasks, and updates using the search bar.',
    disableBeacon: true
  },
  {
    target: '.notify-drp',
    title: 'Stay Informed with Notifications',
    content: 'Keep track of important updates and changes in your projects.',
    disableBeacon: true
  },
  {
    target: '.menuicon',
    title: 'Explore More with the Menu',
    content: 'Access additional features and settings from the menu.',
    disableBeacon: true
  },
  {
    target: '.process-cover',
    title: 'Track Progress with the Graph',
    content: 'Monitor your project’s progress and milestones visually.',
    disableBeacon: true
  },
  {
    target: '.create_project',
    title: 'Get Started with a New Project',
    content: 'Set up a project with all necessary details and team members.',
    disableBeacon: true
  },
  {
    target: '.create_team',
    title: 'Build Your Team',
    content: 'Add and manage team members for efficient collaboration.',
    disableBeacon: true
  },
  {
    target: '.create_task',
    title: 'Streamline Work with Templates',
    content: 'Create templates to standardize tasks and processes.',
    disableBeacon: true
  }
];

//Homapege tutorials steps
export const BlankHomeSteps = [
  {
    target: '.notify-drp',
    title: 'Stay Informed with Notifications',
    content: 'Keep track of important updates and changes in your projects.',
    disableBeacon: true
  },
  {
    target: '.menuicon',
    title: 'Explore More with the Menu',
    content: 'Access additional features and settings from the menu.',
    disableBeacon: true
  },

  {
    target: '.create_project',
    title: 'Get Started with a New Project',
    content: 'Set up a project with all necessary details and team members.',
    disableBeacon: true
  },
  {
    target: '.create_team',
    title: 'Build Your Team',
    content: 'Add and manage team members for efficient collaboration.',
    disableBeacon: true
  },
  {
    target: '.create_task',
    title: 'Streamline Work with Templates',
    content: 'Create templates to standardize tasks and processes.',
    disableBeacon: true
  }
];

// Function to format the timer in MM:SS format
export const formatTime = (timer) => {
  const minutes = Math.floor(timer / 60);
  const seconds = timer % 60;
  return `${String(minutes).padStart(2)}:${String(seconds).padStart(2, '0')}`;
};

// Update user profile functionalities:
export const updateUserDetails = async (formValues, setError, setLoading, showToast = true) => {
  if (setLoading) {
    setLoading(true);
  }
  try {
    let formData = { ...formValues };
    const form = new FormData();
    Object.entries(formData).forEach(([key, value]) => form.append(key, value));

    formData.phoneNumber = getPlainPhoneNumber(formData.phoneNumber);
    const response = await APIServices.post('/user/update-profile', form, {
      'Content-Type': 'multipart/form-data'
    });
    console.log(response);

    if (response.message && response.status === 200) {
      if (showToast) appToast(response.message, TOAST_TYPE.SUCCESS);
      return response;
    } else {
      if (showToast) appToast(response.data.message || ProfileCompletionStrings.errorOccurred, TOAST_TYPE.ERROR);
      return false;
    }
  } catch (err) {
    setError(ProfileCompletionStrings.failedToRegister);
    console.error('Error while Updating profile:', err);
  } finally {
    if (setLoading) {
      setLoading(false);
    }
  }
};

// Helper function to format UTC offsets
export const getUtcOffsetLabel = (timezone) => {
  const offset = moment.tz(timezone).utcOffset();
  const sign = offset >= 0 ? '+' : '-';
  const hours = Math.floor(Math.abs(offset) / 60);
  const minutes = Math.abs(offset) % 60;
  return `(UTC${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')})`;
};

//function to validate the form of overview/details page
export const validateDetailsTabForm = (formValues, reccurenceValues, setErrors, isInternalProject = false) => {
  let newErrors = {};

  if (!validateTextInput(formValues.projectName).isValid) {
    if (!validateTextInput(formValues.projectName).cleanedValue) {
      newErrors.projectName = 'Project Name is required.';
    } else {
      newErrors.projectName = validateTextInput(formValues.projectName).message;
    }
  }

  if (!isInternalProject && !formValues.organizationId) {
    newErrors.organizationId = 'Please select an organization.';
  }
  if (!formValues.isRecurring) {
    if (!formValues.startDate) {
      newErrors.startDate = 'Project Start Date is required.';
    }

    if (!formValues.endDate) {
      newErrors.endDate = 'Project Due Date is required.';
    } else if (formValues.startDate && formValues.endDate && formValues.endDate < formValues.startDate) {
      newErrors.endDate = 'DueDate must be after the Start Date.';
    }
  }
  // 🔽 Recurrence validation block (only if recurring)
  if (formValues.isRecurring) {
    const { recurrenceFrequency, recurrenceStartDate, dateType, recurrenceCycleCount } = reccurenceValues;

    if (!recurrenceFrequency) {
      newErrors.recurrenceFrequency = 'Recurrence frequency is required.';
    }

    if (!dateType) {
      newErrors.dateType = 'Date type is required.';
    }
    if (!recurrenceStartDate) {
      newErrors.recurrenceStartDate = 'Start date is required for ongoing recurrence.';
    }

    if (dateType === '68488873fbd299f348c1e528') {
      //cycles datetype
      if (!recurrenceCycleCount) {
        newErrors.recurrenceCycleCount = 'Recurrence cycle count is required.';
      }
    }
  }

  // if (!formValues.estimatedPrice) {
  //   newErrors.estimatedPrice = "Estimated Value is required.";
  // } else if (Number(formValues.estimatedPrice) <= 0) {
  //   newErrors.estimatedPrice = "Estimated Value must be a positive number.";
  // }

  setErrors(newErrors);

  // Disable submit button if there are any errors or missing fields
  // setIsSubmitDisabled(Object.keys(newErrors).length > 0);
  return Object.keys(newErrors).length > 0;
};

export const validateMilestoneFields = (milestones, setErrors, saveTemplate, dateDiff) => {
  let hasError = false;
  const newErrors = {};

  milestones.forEach((milestone, index) => {
    newErrors[index] = {};
    if (!validateTextInput(milestone.name).isValid) {
      if (!validateTextInput(milestone.name).cleanedValue) {
        newErrors[index].name = 'Milestone name is required.';
      } else {
        newErrors[index].name = validateTextInput(milestone.name).message;
      }
      hasError = true;
    }
    // if (!validateTextInput(milestone.description).isValid) {
    //   if (!validateTextInput(milestone.description).cleanedValue) {
    //     newErrors[index].description = 'Description is required.';
    //   } else {
    //     newErrors[index].description = validateTextInput(milestone.description).message;
    //   }
    //   hasError = true;
    // }

    // if (milestone.signOffRequired && milestone.signOffTime === '') {
    //   console.log(milestone.signOffTime);

    //   newErrors[index].signOffTime = 'SignOff Time is required.';
    //   hasError = true;
    // }
    if (milestone.signOffRequired && milestone.signOffTime > dateDiff) {
      newErrors[index].signOffTime = `SignOff Time cannot exceed the allowed limit of ${dateDiff} days.`;
      hasError = true;
    }
    if (milestone.daysToComplete == null) {
      newErrors[index].daysToComplete = 'Days to Completion is required.';
      hasError = true;
    }

    if (milestone.daysToComplete && milestone.daysToComplete > dateDiff) {
      newErrors[index].daysToComplete = `Days to completion cannot exceed the allowed limit of ${dateDiff} days.`;
      hasError = true;
    }
    // if (milestone.daysToComplete && milestone.daysToComplete == 0) {
    //   newErrors[index].daysToComplete = `Days to completion cannot be 0.`;
    //   hasError = true;
    // }
    if (milestone.paymentDueRequired && !milestone.paymentDue) {
      newErrors[index].paymentDue = 'Payment Due is required.';
      hasError = true;
    }
    if (milestone.paymentDueRequired && milestone.paymentDue === 0) {
      newErrors[index].paymentDue = 'Payment Due cannot be 0.';
      hasError = true;
    }
  });
  console.log(newErrors);

  setErrors(newErrors);
  return !hasError;
};

// function to calculate the diff between two dates.. used in milstone add template.
export const calculateDateDiff = (start, end) => {
  const startDate = new Date(start);
  const endDate = new Date(end);

  // Get the time difference in milliseconds
  const timeDiff = endDate - startDate;

  // Convert milliseconds to days
  return Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
};

// Function to format date in this format "Oct 1" or "Oct 1, 2025" based on showYear
export const formatDate = (dateString, showYear = false, format = null) => {
  const date = new Date(dateString);

  // If a custom format is provided, return formatted string
  if (format) {
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();

    return format
      ?.replace(/DD/i, day)
      ?.replace(/MM/i, month)
      ?.replace(/YYYY/i, year)
      ?.replace(/YY/i, String(year).slice(-2));
  }

  // Default existing behavior
  let options = {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric'
  };

  if (showYear) {
    options.year = 'numeric';
  }

  return new Intl.DateTimeFormat('en-US', options).format(date);
};

// make list of the data which is coming fromt he backend
export const formatOptions = (options) => {
  const formattedOptions = options.map((option) => ({
    value: option._id,
    label: option.name
  }));

  return formattedOptions;
};

export const daysOptions = [
  { value: 0, label: '0 Day' },
  { value: 1, label: '1 Day' },
  { value: 2, label: '2 Days' },
  { value: 3, label: '3 Days' },
  { value: 4, label: '4 Days' },
  { value: 5, label: '5 Days' },
  { value: 6, label: '6 Days' },
  { value: 7, label: '7 Days' }
];

export const projectFilters = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'closed', label: 'Closed' }
];

export const calculateDateDifference = (inputDate = new Date(), today = new Date(), showHrs = false) => {
  const givenDate = new Date(inputDate);

  const diffInMilliseconds = givenDate - today;

  const MS_IN_DAY = 1000 * 60 * 60 * 24;
  const MS_IN_HOUR = 1000 * 60 * 60;
  const MS_IN_MIN = 1000 * 60;

  // Days
  const diffInDays = Math.floor(diffInMilliseconds / MS_IN_DAY);

  // Remaining after days
  const remainingAfterDays = diffInMilliseconds % MS_IN_DAY;

  const hours = Math.floor(remainingAfterDays / MS_IN_HOUR);
  const minutes = Math.floor((remainingAfterDays % MS_IN_HOUR) / MS_IN_MIN);

  // 👉 FUTURE DATE
  if (diffInDays > 0) {
    if (showHrs) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ${hours} hour${
        hours !== 1 ? 's' : ''
      } ${minutes} minute${minutes !== 1 ? 's' : ''} `;
    }

    return diffInDays === 1 ? '1 Day' : `${diffInDays + 1} Days`;
  }

  // 👉 SAME DAY
  if (diffInDays === 0) {
    if (showHrs) {
      const totalHours = Math.floor(diffInMilliseconds / MS_IN_HOUR);
      const mins = Math.floor((diffInMilliseconds % MS_IN_HOUR) / MS_IN_MIN);

      return `${totalHours} hour${totalHours !== 1 ? 's' : ''} ${mins} minute${mins !== 1 ? 's' : ''}`;
    }

    // existing behavior (HH:MM:SS)
    const hours = Math.floor(diffInMilliseconds / MS_IN_HOUR);
    const minutes = Math.floor((diffInMilliseconds % MS_IN_HOUR) / MS_IN_MIN);
    const seconds = Math.floor((diffInMilliseconds % MS_IN_MIN) / 1000);

    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  // 👉 PAST DATE
  return 'Overdue';
};

const toEST = (date) => {
  return new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' }));
};

// Format date and time for chat with the same format and time preference
export const formatDateTime = (
  dateString,
  dateFormat = 'MM/DD/YYYY',
  timeFormat = '12hour',
  isMessage = false,
  showYesterday = true
) => {
  const date = new Date(dateString);
  const estDate = toEST(date); // Convert to EST
  const today = new Date();

  // Reset today's date to midnight for easier comparison
  today.setHours(0, 0, 0, 0);

  // Calculate yesterday by subtracting one day from today's midnight time
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  // Helper function to format date based on the provided format
  const formatDate = (date, format) => {
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    };
    switch (
      format.toUpperCase() // Ensure case insensitivity
    ) {
      case 'DD/MM/YYYY':
        return estDate.toLocaleDateString('en-GB', options);
      case 'MM/DD/YYYY':
        return estDate.toLocaleDateString('en-US', options);
      case 'YYYY/MM/DD':
        return estDate.toISOString().split('T')[0];
      default:
        return estDate.toLocaleDateString(); // Fallback to default format
    }
  };

  // Helper function to format time based on the 12-hour or 24-hour preference
  const formatTime = (date, format) => {
    return date.toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: format === '12hour' // Use 12-hour format if specified
    });
  };

  // Determine if the date is today, yesterday, or neither
  if (date >= today) {
    if (isMessage) {
      return `today at ${formatTime(date, timeFormat)}`;
    }
    return `- ${formatTime(date, timeFormat)} Today`;
  } else if (date >= yesterday) {
    if (showYesterday) {
      if (isMessage) {
        return `yesterday at ${formatTime(date, timeFormat)}`;
      }
      return `- ${formatTime(date, timeFormat)} Yesterday`;
    } else {
      return `${formatDate(date, dateFormat)} at ${formatTime(date, timeFormat)}`;
    }
  } else {
    if (isMessage) {
      return `on ${formatDate(date, dateFormat)} at ${formatTime(date, timeFormat)}`;
    }
    return `- ${formatTime(date, timeFormat)} ${formatDate(date, dateFormat)}`;
  }
};

export const adjustDate = (dateString, days) => {
  // Parse the input date string into a Date object
  const date = new Date(dateString);

  // Add or subtract the days
  date.setDate(date.getDate() + days);

  // Format the date to "Month Day"
  const formattedDate = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric'
  }).format(date);

  // Return the updated date in ISO 8601 format
  return formattedDate;
};

export const getDaysUntilMidnight = (dateStr) => {
  const now = new Date();

  // Set the target date to midnight (start of the day)
  const inputDate = new Date(dateStr);
  inputDate.setHours(0, 0, 0, 0); // force to 12:00 AM

  const diffMs = inputDate - now;
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  return days;
};

// function to handle the roles of users:
// rolePermissions.js
export const ROLE_PERMISSIONS = {
  owner: [
    // "all_permissions",
    'update_team_page',
    'create_projects',
    'update_projects',
    'edit_milestones',
    'send_signoff_requests',
    'change_request',
    'feedback_request',
    'change_order',
    'add_qoute',
    'close_req',
    'modify_team',
    'plan_details'
    // "modify_clients",
  ],
  team_admin: [
    // "all_permissions",
    'update_team_page',
    'create_projects',
    'update_projects',
    'edit_milestones',
    'send_signoff_requests',
    'change_request',
    'feedback_request',
    'change_order',
    'add_qoute',
    'close_req',
    'modify_team'
    // "modify_clients",
  ],
  team_member: ['update_projects', 'send_signoff_requests', 'change_request', 'feedback_request', 'change_order'],
  contractor: ['update_projects'],
  consultant: ['update_projects'],
  admin: [
    'change_order',
    'change_request',
    'approve_sign_off',
    'approve_qoute',
    'feedback_give'
    // "modify_team",
  ],
  user: ['change_request', 'change_order', 'feedback_give']
};

export const hasPermission = (role, permission, memberPermission = 'update', isInternalProject = false) => {
  if (!ROLE_PERMISSIONS[role]) {
    return false; // Role not found
  }

  // Check if it's an internal project and the role is owner or team_admin
  if (isInternalProject && (role === 'owner' || (role === 'team_admin' && memberPermission !== 'View'))) {
    return true; // All permissions granted for internal projects
  }

  // Owner has all permissions
  if (ROLE_PERMISSIONS[role].includes('all_permissions')) {
    return true;
  }

  if (memberPermission === 'View' && role !== 'admin' && role !== 'user') {
    return false;
  }
  // Check if the permission exists for the role
  return ROLE_PERMISSIONS[role].includes(permission);
};

export const getPriorityStatus = (name) => {
  if (!name) return 'default';

  const mapping = {
    HIGH: 'warning',
    URGENT: 'error',
    NONE: 'primary',
    LOW: 'info',
    'IN PROGRESS': 'info',
    COMPLETE: 'success'
  };

  return mapping[name.toUpperCase()] || 'default';
};

//clears the data of milestones stored from the create
export const clearLocalStorage = () => {
  localStorage.removeItem('formData');
};

// Function to replace user mentions with highlighted names
export const renderHighlightedMessage = (activity) => {
  let formattedMessage = activity.message;

  activity.message_user_tags.forEach((tag) => {
    const mentionRegex = new RegExp(`@${tag.tagname}`, 'g');
    formattedMessage = formattedMessage.replace(mentionRegex, `<span class="lightblue-text">@${tag.tagname}</span>`);
  });

  return formattedMessage;
};

export const formatPricing = (value) => {
  if (!value) return '';
  const [integerPart, decimalPart] = value?.split('.');
  const formattedInteger = parseInt(integerPart || '0', 10).toLocaleString('en-US');
  return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

export const cleanFormattedPricing = (formattedValue) => {
  if (!formattedValue) return '';
  const cleanValue = String(formattedValue)?.replace(/,/g, ''); // Remove commas
  return cleanValue?.includes('.') ? parseFloat(cleanValue) : parseInt(cleanValue, 10);
};

export const validPricing = (price) => {
  // Only allow digits and period for decimal values
  const validValue = price
    // Allow only digits and a single period
    .replace(/[^0-9.]/g, '')
    // Prevent multiple periods
    .replace(/(\..*)\./g, '$1')
    // Restrict to two decimal places
    .replace(/^(\d+\.?\d{0,2}).*$/, '$1')
    // Remove leading zeros (except for 0 before the decimal point)
    .replace(/^0+(?!\.|$)/, '');

  return validValue;
};

export const updateUserFormValues = ({ userDetails, setFormValues, setVerified }) => {
  const {
    email,
    countryDialCode,
    phoneNumber,
    fullName,
    countryCode,
    profilePic,
    companyId,
    isTotorialVisited,
    isEmailVerified,
    isPhoneNumberVerified
  } = userDetails;

  // Update form values
  setFormValues((prevValues) => ({
    ...prevValues,
    email,
    countryDialCode,
    phoneNumber: getformattedPhoneNumber(phoneNumber),
    fullName,
    countryCode,
    profilePic,
    companyName: companyId?.name,
    isTotorialVisited
  }));

  // Update verification status
  setVerified({
    isEmailVerified,
    isPhoneNumberVerified
  });
};

export const getOrgUserRole = (user, orgId) => {
  return user?.organizations?.find((x) => x.organizationId._id === orgId && x?.isActive);
};

export const convertUnixToDate = (timestamp) => {
  // Convert seconds to milliseconds
  const date = new Date(timestamp);
  const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are zero-based
  const day = String(date.getUTCDate()).padStart(2, '0');
  const year = date.getUTCFullYear();
  return `${month}/${day}/${year}`;
};

export const getPackClass = (value, isActive) => {
  if (!isActive) {
    return 'red-btn';
  } else if (value === 'in_trial') {
    return 'purple-btn';
  } else {
    return 'green-btn';
  }
};

//array of days for the updates tab in create project/setting
export const daysArray = [
  { id: 'S', value: 'Sunday' },
  { id: 'M', value: 'Monday' },
  { id: 'T', value: 'Tuesday' },
  { id: 'W', value: 'Wednesday' },
  { id: 'T', value: 'Thursday' },
  { id: 'F', value: 'Friday' },
  { id: 'S', value: 'Saturday' }
];

export const weeklyDaysOptions = [
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
  { value: 'sunday', label: 'Sunday' }
];

export const FrequencyOptions = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'bi-weekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'annually', label: 'Annually' }
];

export const dateTypeOptions = [
  { label: 'Ongoing', value: 'ongoing' },
  { label: 'Fixed', value: 'fixed' }
];

export const isFutureDate = (dateString, checkPastDate = false) => {
  const inputDate = new Date(dateString);
  const now = new Date();

  // Zero out the time part of both dates
  inputDate.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);

  return inputDate > now;
};

export const isPastDate = (dateString) => {
  const inputDate = new Date(dateString);
  const now = new Date();

  inputDate.setHours(0, 0, 0, 0);

  return inputDate < now;
};

export const fetchAndFormatUserDetails = async (logout, fetchUserInfo, user = null) => {
  let userDetails;
  const token = getCookies('authToken');
  if (!token) return null; // ✅ early return

  try {
    if (user) {
      userDetails = user;
    } else {
      userDetails = await fetchUserInfo();
    }
    if (!userDetails) return null;

    if (!userDetails.companyId) {
      logout?.(); // optional chaining in case logout isn't passed
      return null;
    }

    const {
      email,
      countryDialCode,
      phoneNumber,
      fullName,
      countryCode,
      profilePic,
      companyId,
      isTotorialVisited,
      isEmailVerified,
      isPhoneNumberVerified
    } = userDetails;

    // ✅ return processed data (no direct state updates here)
    return {
      formValues: {
        email,
        countryDialCode,
        phoneNumber: getformattedPhoneNumber(phoneNumber),
        fullName,
        countryCode,
        profilePic,
        companyName: companyId?.name,
        isTotorialVisited
      },
      verified: {
        isEmailVerified,
        isPhoneNumberVerified
      }
    };
  } catch (error) {
    console.error('Error fetching user details:', error);
    return null;
  }
};

export const getPlainText = (html) => {
  if (html) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }
  return '';
};

export const copyToClipboard = (text) => {
  let url = process.env.REACT_APP_FRONTEND_BASE_URL + '/#' + text;
  navigator.clipboard
    .writeText(url)
    .then(() => {
      boardToast('Link copied!', TOAST_TYPE.SUCCESS); // ✅ feedback
    })
    .catch((err) => {
      console.error('Failed to copy: ', err);
      boardToast('Failed to copy link', TOAST_TYPE.ERROR);
    });
};

export const labelColors = [
  { color: '#1868DB', textColor: '#FFFFFF' }, // Blue → white text
  { color: '#227D9B', textColor: '#FFFFFF' }, // Teal → white
  { color: '#5A7F24', textColor: '#FFFFFF' }, // Olive green → white
  { color: '#AD4887', textColor: '#FFFFFF' }, // Purple → white
  { color: '#6C6E75', textColor: '#FFFFFF' }, // Gray → white
  { color: '#B9F4DC', textColor: '#145C3A' }, // Light mint → dark green text
  { color: '#F5E98A', textColor: '#6B5900' }, // Yellow → dark golden text
  { color: '#FCE3A6', textColor: '#7A5B00' }, // Light yellow → dark brown text
  { color: '#FFD5D2', textColor: '#8C2B23' }, // Light pink → dark red text
  { color: '#EDD7FD', textColor: '#5C2C91' }, // Lavender → dark purple text
  { color: '#D0E1FE', textColor: '#204A87' }, // Light blue → dark blue text
  { color: '#C5ECFB', textColor: '#0E5A73' }, // Sky blue → dark teal text
  { color: '#D3F1A6', textColor: '#406B00' }, // Light green → dark green text
  { color: '#FDD0EC', textColor: '#8C2C65' }, // Pink → magenta text
  { color: '#DDDEE0', textColor: '#333333' }, // Light gray → dark gray text
  { color: '#4BCD97', textColor: '#FFFFFF' }, // Green → white
  { color: '#EDD128', textColor: '#5E4C00' }, // Bright yellow → dark golden text
  { color: '#FCA603', textColor: '#5A2E00' }, // Orange → dark brown text
  { color: '#F87169', textColor: '#6B1610' }, // Coral → dark red text
  { color: '#C97CF3', textColor: '#4C0073' }, // Violet → deep purple text
  { color: '#21835A', textColor: '#FFFFFF' }, // Dark green → white
  { color: '#946F01', textColor: '#FFFFFF' }, // Mustard brown → white
  { color: '#BE5A00', textColor: '#FFFFFF' }, // Burnt orange → white
  { color: '#CA372C', textColor: '#FFFFFF' }, // Dark red → white
  { color: '#954AC0', textColor: '#FFFFFF' }, // Purple → white
  { color: '#679DF0', textColor: '#FFFFFF' }, // Strong blue → white
  { color: '#6BC2E0', textColor: '#073D52' }, // Cyan → dark teal text
  { color: '#E774BB', textColor: '#5E0037' }, // Pink → dark magenta text
  { color: '#8C8E97', textColor: '#FFFFFF' }, // Medium gray → white
  { color: '#95C649', textColor: '#2E4B00' } // Lime green → dark green text
];

export const createProjectBoardSocket = (userId, projectBoardId) => {
  if (!userId || !projectBoardId) return null;

  const baseUrl = process.env.REACT_APP_BASE_URL?.replace(/\/+$/, '');
  const socket = io(`${baseUrl}/project-board`, {
    query: { userId, projectBoardId }
  });

  socket.on('connect', () => {
    console.log(`✅ Connected to board ${projectBoardId}`);
  });

  socket.on('disconnect', () => {
    console.log(`❌ Disconnected from board ${projectBoardId}`);
  });

  socket.on('connect_error', (err) => {
    console.error(`⚠️ Failed to connect to board ${projectBoardId}:`, err.message);
  });

  return socket;
};

export const createProjectMasterBoardSocket = (userId, milestones) => {
  if (!userId || !Array.isArray(milestones) || milestones.length === 0) {
    return null;
  }

  // Extract unique projectBoardIds from milestones
  const projectBoardIds = [...new Set(milestones.map((m) => m.projectBoardId?._id).filter(Boolean))];

  if (projectBoardIds.length === 0) return null;

  // Pass all projectBoardIds in query as JSON string
  const baseUrl = process.env.REACT_APP_BASE_URL?.replace(/\/+$/, '');
  const socket = io(`${baseUrl}/project-board`, {
    query: {
      userId,
      projectBoardIds: JSON.stringify(projectBoardIds)
    }
  });

  socket.on('connect', () => {
    console.log(`✅ Connected to boards: ${projectBoardIds.join(', ')}`);
  });

  socket.on('disconnect', () => {
    console.log(`❌ Disconnected from boards: ${projectBoardIds.join(', ')}`);
  });

  socket.on('connect_error', (err) => {
    console.error(`⚠️ Failed to connect to boards: ${projectBoardIds.join(', ')}`, err.message);
  });

  return socket;
};

// utils/sortMilestones.js
export const sortMilestones = (milestones = []) => {
  const priorityOrder = {
    urgent: 5,
    high: 4,
    medium: 3,
    low: 2,
    none: 1
  };

  return [...milestones].sort((a, b) => {
    const pa = priorityOrder[a?.priority?.value?.toLowerCase()] || 0;
    const pb = priorityOrder[b?.priority?.value?.toLowerCase()] || 0;

    // Sort by priority first (desc)
    if (pb !== pa) return pb - pa;

    // Optional: then by creation date (newest first)
    if (a.createdAt && b.createdAt) {
      return new Date(b.createdAt) - new Date(a.createdAt);
    }

    return 0;
  });
};

export const reminderOptions = [
  { value: 'none', label: 'None' },
  { value: 'on_due', label: 'On due date' },
  { value: '1_day', label: '1 day before' },
  { value: '2_days', label: '2 days before' },
  { value: '1_week', label: '1 week before' }
];

// "2:15 pm" -> { h: 14, m: 15 }
export const parseTimeStringToHM = (timeStr) => {
  if (!timeStr) return null;
  const [hhmm, period] = timeStr.split(' ');
  const [hStr, mStr] = hhmm.split(':');
  let h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (period?.toLowerCase() === 'pm' && h !== 12) h += 12;
  if (period?.toLowerCase() === 'am' && h === 12) h = 0;
  return { h, m };
};

// Combine date + time into a Date and log it
export const logCombined = (dateObj, timeStr) => {
  if (!dateObj) return;
  const d = new Date(dateObj); // clone so we don't mutate state
  d.setSeconds(0, 0);
  const hm = parseTimeStringToHM(timeStr);
  if (hm)
    d.setHours(hm.h, hm.m, 0, 0); // apply selected time
  else d.setHours(0, 0, 0, 0); // no time yet -> midnight
  console.log(d.toString()); // e.g. "Sat Oct 18 2025 05:00:00 GMT+0530 (India Standard Time)"
  return d.toString();
};

export const findLetterFromName = (value) => {
  if (!value) return '';

  const trimmed = value.trim();

  // ✅ If there is no space (single word like "tj")
  if (!trimmed.includes(' ')) {
    return trimmed.slice(0, 2).toUpperCase();
  }

  const name = trimmed.split(' ').filter(Boolean);

  // ✅ If only one word after split
  if (name.length === 1) {
    return name[0][0]?.toUpperCase();
  }

  // ✅ If multiple words → first letters of first two words
  return name[0][0]?.toUpperCase() + name[1][0]?.toUpperCase();
};

export const hasLink = (html) => {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.querySelector('a') !== null;
};

export const uploadFilesToApi = async (files, isSubtask, parentId) => {
  const formData = new FormData();

  files.forEach((file) => {
    formData.append('files', file); // backend expects "files"
  });

  const url = isSubtask ? `/milestone-subtask/${parentId}/attachments` : `/project-milestone/${parentId}/attachments`;

  const response = await APIServices.post(url, formData, {
    'Content-Type': 'multipart/form-data'
  });

  return response; // array of uploaded files
};

export const calculateTimeAgo = (date) => {
  if (!date) return 'Just now';

  const time = new Date(date).getTime();
  if (isNaN(time)) return 'Just now';

  const diffMs = Date.now() - time;

  const minutes = Math.floor(diffMs / (1000 * 60));
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;

  return `${days} day${days > 1 ? 's' : ''} ago`;
};

export const isDraftedProject = (project) => {
  if (project.isDrafted) {
    return true;
  }
  return false;
};

const isMidnightUTC = (d) =>
  d instanceof Date &&
  d.getUTCHours() === 0 &&
  d.getUTCMinutes() === 0 &&
  d.getUTCSeconds() === 0 &&
  d.getUTCMilliseconds() === 0;

const toAllDayDateStr = (d) => d.toISOString().slice(0, 10);

export const withEndTime = (dateStr) => {
  if (!dateStr) return { start: null, end: null, allDay: false };
  const startDate = new Date(dateStr);
  if (isNaN(startDate)) return { start: null, end: null, allDay: false };
  if (isMidnightUTC(startDate)) {
    return { start: toAllDayDateStr(startDate), end: null, allDay: true };
  }
  const end = new Date(startDate.getTime() + 5 * 60 * 1000);
  return { start: startDate, end, allDay: false };
};

export const formatTeamData = (teams) =>
  teams?.map((value) => ({
    ...value.userId,
    permission: value.permission?.name || 'View'
  }));

const normalizeLabelIds = (labels) =>
  (Array.isArray(labels) ? labels : [])
    .map((label) => (typeof label === 'string' ? label : label?._id))
    .filter(Boolean);

const flattenChecklistTree = (checklists = [], milestone, options = {}) => {
  const { isSubtaskChecklist = false, subtaskId = null, subtaskName = null } = options;

  const result = [];

  const traverse = (items) => {
    items.forEach((ch) => {
      if (ch.dueDate) {
        const { start, end } = withEndTime(ch.dueDate);

        result.push({
          id: ch._id,
          title: ch.name,
          start,
          end,
          isCompleted: ch.isCompleted,
          type: isSubtaskChecklist ? 'subtask-checklist' : 'checklist',
          className: isSubtaskChecklist ? 'subtask-checklist-event' : undefined,
          milestoneId: milestone._id,
          allDay: !ch?.dueDateString?.includes(':'),
          teams: formatTeamData(milestone?.projectBoardId?.teams) || [],
          parentProjectBoardId: milestone?.projectBoardId?._id,
          assigneeIds: ch.assignees || [],
          labelIds: normalizeLabelIds(ch.labelIds),
          isSubtaskChecklist,
          subtaskId,
          subtaskName,
          projectName: milestone?.projectBoardId?.name,
          milestoneName: milestone?.name
        });
      }

      // 🔥 RECURSION — THIS IS THE KEY
      if (ch.children?.length) {
        traverse(ch.children);
      }
    });
  };

  traverse(checklists);
  return result;
};

export const buildEvents = (milestones) => {
  return milestones.flatMap((m) => {
    const { start, end } = withEndTime(m.kbDueDate);
    const milestoneEvent = {
      id: m._id,
      title: m.name,
      start,
      end,
      type: 'milestone',
      milestoneId: m._id,
      teams: formatTeamData(m?.projectBoardId?.teams) || [],
      assigneeIds: m.assigneeIds || [],
      parentProjectBoardId: m?.projectBoardId?._id,
      projectName: m?.projectBoardId?.name,
      milestoneName: m?.name,
      allDay: !m?.kbDueDateString?.includes(':'),
      isCompleted: m?.isCompleted || false,
      priorityId: m?.priority?._id,
      labelIds: normalizeLabelIds(m.labelIds)
    };

    // const checklistEvents = (m.checklist || [])
    //   .filter((ch) => ch.dueDate)
    //   .map((ch) => {
    //     const { start, end } = withEndTime(ch.dueDate);
    //     return {
    //       id: ch._id,
    //       title: ch.name,
    //       start,
    //       end,
    //       isCompleted: ch.isCompleted,
    //       type: 'checklist',
    //       milestoneId: m._id,
    //       allDay: !ch?.dueDateString?.includes(':'),
    //       milestoneName: m.name,
    //       teams: formatTeamData(m?.projectBoardId?.teams) || [],
    //       parentProjectBoardId: m?.projectBoardId?._id,
    //       assigneeIds: ch.assignees || [],
    //       isSubtaskChecklist: false,
    //       projectName: m?.projectBoardId?.name
    //     };
    //   });

    const checklistEvents = flattenChecklistTree(m.checklist || [], m, {
      isSubtaskChecklist: false
    });

    const subtasksEvents = (m.subtask || []).flatMap((sub) => {
      const { start, end } = withEndTime(sub.dueDate);
      const subtaskEvent = {
        id: sub._id,
        title: sub.name,
        start,
        end,
        type: 'subtask',
        milestoneId: m._id,
        allDay: !sub?.dueDateString?.includes(':'),
        teams: formatTeamData(m?.projectBoardId?.teams) || [],
        parentProjectBoardId: m?.projectBoardId?._id,
        assigneeIds: sub.assignees || [],
        labelIds: normalizeLabelIds(sub.labelIds),
        subtaskId: sub._id,
        status: sub.status,
        projectName: m?.projectBoardId?.name,
        milestoneName: m?.name
      };

      // const subtaskChecklistEvents = (sub.checklist || [])
      //   .filter((ch) => ch.dueDate)
      //   .map((ch) => ({
      //     id: ch._id,
      //     title: ch.name,
      //     start: ch.dueDate,
      //     isCompleted: ch.isCompleted,
      //     type: 'subtask-checklist',
      //     className: 'subtask-checklist-event',
      //     milestoneId: m._id,
      //     allDay: !ch?.dueDateString?.includes(':'),
      //     teams: formatTeamData(m?.projectBoardId?.teams) || [],
      //     parentProjectBoardId: m?.projectBoardId?._id,
      //     subtaskId: sub._id,
      //     assigneeIds: ch.assignees || [],
      //     isSubtaskChecklist: true,
      //     subtaskName: sub.name,
      //     projectName: m?.projectBoardId?.name,
      //     milestoneName: m?.name
      //   }));

      const subtaskChecklistEvents = flattenChecklistTree(sub.checklist || [], m, {
        isSubtaskChecklist: true,
        subtaskId: sub._id,
        subtaskName: sub.name
      });

      return [subtaskEvent, ...subtaskChecklistEvents];
    });

    return [milestoneEvent, ...checklistEvents, ...subtasksEvents];
  });
};

export const CustomToggleforChecklist = React.forwardRef(({ children, onClick }, ref) => (
  <div
    style={{ cursor: 'pointer' }}
    onClick={(e) => {
      e.stopPropagation();
      onClick(e);
    }}>
    {children}
  </div>
));

export const buildReorderPayload = (sectionId, sourceIndex, destIndex, sections) => {
  const section = sections.find((s) => s._id === sectionId);
  if (!section) return [];

  const items = [...(section.children || [])];

  // 🔁 reorder locally
  const [moved] = items.splice(sourceIndex, 1);
  items.splice(destIndex, 0, moved);

  // 🧠 build payload
  return items.map((item, index) => ({
    id: item._id,
    order: index,
    parentChecklistId: sectionId
  }));
};

export const buildCrossSectionPayload = (draggableId, sourceSectionId, destSectionId, destIndex, sections) => {
  const sourceSection = sections.find((s) => s._id === sourceSectionId);
  const destSection = sections.find((s) => s._id === destSectionId);

  if (!sourceSection || !destSection) return [];

  const sourceItems = [...(sourceSection.children || [])];
  const destItems = [...(destSection.children || [])];

  // 🔥 remove from source
  const sourceIndex = sourceItems.findIndex((i) => i._id === draggableId);
  if (sourceIndex === -1) return [];

  const [movedItem] = sourceItems.splice(sourceIndex, 1);

  // 🔥 insert into destination
  destItems.splice(destIndex, 0, movedItem);

  // 🧠 build payload for BOTH sections
  const sourcePayload = sourceItems.map((item, index) => ({
    id: item._id,
    order: index,
    parentChecklistId: sourceSectionId
  }));

  const destPayload = destItems.map((item, index) => ({
    id: item._id,
    order: index,
    parentChecklistId: destSectionId
  }));

  return [...sourcePayload, ...destPayload];
};

export const flattenChecklistItems = (items = []) => {
  const result = [];

  const stack = [...items];

  while (stack.length) {
    const current = stack.pop();

    if (current?.children?.length) {
      result.push(...current.children);
      stack.push(...current.children);
    }
  }

  return result;
};

export const normalizePastedText = (text) => {
  return text
    .split(/\r?\n/)
    .map((line) =>
      line
        .replace(/^\s*[\u2022•\-*\d+.)]+\s*/, '') // remove bullets/numbers
        .trim()
    )
    .filter(Boolean)
    .join('\n');
};

export const extractScopeFromContract = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file); // ✅ MUST be "file"

    const response = await APIServices.post('/project-scope/extract-scope-from-pdf', formData, {
      'Content-Type': 'multipart/form-data'
    });
    if (response?.data?.message) {
      appToast(response.data.message, TOAST_TYPE.ERROR);
      return null;
    }
    return response;
  } catch (error) {
    console.error('Extract scope error:', error);
    appToast('Failed to extract contract.', TOAST_TYPE.ERROR);
  }
};

export const calculateDays = (dueDate) => {
  if (!dueDate) return 0;

  const today = new Date();
  const due = new Date(dueDate);

  const diffTime = due.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays > 0 ? diffDays : 0;
};

export const validateMilestonesForScope = (milestones) => {
  // if (!milestones.length) {
  //   appToast('At least one milestone is required.', TOAST_TYPE.ERROR);
  //   return false;
  // }

  for (let i = 0; i < milestones.length; i++) {
    const m = milestones[i];

    if (!m.name || !m.name.trim()) {
      appToast(`Milestone ${i + 1}: Name is required`, TOAST_TYPE.ERROR);
      return false;
    }

    // if (!m.paymentDue || Number(m.paymentDue) <= 0) {
    //   appToast(`Milestone ${i + 1}: Payment due is required`, TOAST_TYPE.ERROR);
    //   return false;
    // }

    // if (!m.dueDate) {
    //   appToast(`Milestone ${i + 1}: Due date is required`, TOAST_TYPE.ERROR);
    //   return false;
    // }
  }

  return true;
};

export const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const todayStart = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

export const handleDownloadPdf = async (fileUrl, filename) => {
  try {
    const response = await APIServices.get(`/link/download?url=${fileUrl}`, {
      responseType: 'blob'
    });

    const blob = new Blob([response]);
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  } catch (err) {
    console.error(err);
  }
};

export const getDaysBetweenDates = (startDate, dueDate) => {
  if (!startDate || !dueDate) return '';

  const start = new Date(startDate);
  const end = new Date(dueDate);

  // Normalize both to midnight to avoid time issues
  start.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const diffTime = end - start;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays >= 0 ? diffDays : 0;
};

export const getDateFormat = (format) => {
  switch (format) {
    case 'DD/MM/YYYY':
      return 'dd/MM/yyyy';
    case 'MM/DD/YYYY':
      return 'MM/dd/yyyy';
    case 'YYYY/MM/DD':
      return 'yyyy/MM/dd';
    default:
      return 'MM/dd/yyyy';
  }
};

export const getRemainingDays = (endDate) => {
  if (!endDate) return 0;

  const today = new Date();
  const end = new Date(endDate);

  // remove time for accurate day diff
  today.setHours(0, 0, 0, 0);
  end.setHours(0, 0, 0, 0);

  const diffTime = end - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return diffDays;
};

export const validateChangeOrder = (formData, setErrors) => {
    const newErrors = {};
    if (!validateTextInput(formData.title).isValid) {
      if (!validateTextInput(formData.title).cleanedValue) {
        newErrors.title = "Request Title is required.";
      } else {
        newErrors.title = validateTextInput(formData.title).message;
      }
    }

    if (!validateTextInput(formData.description).isValid) {
      if (!validateTextInput(formData.description).cleanedValue) {
        newErrors.description = "Request Description is required.";
      } else {
        newErrors.description = validateTextInput(formData.description).message;
      }
    }

    // if (files.length === 0) newErrors.files = "At least one file must be attached.";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
};

export const formatCurrency = (value) => {
  return Number(value || 0).toLocaleString();
};

export const isOptionEmpty = (option) => {
  const hasLineItemValue = option.lineItems?.some(
    (item) => item.title?.trim()
  );

  return (
    !option.type?.trim() &&
    !option.title?.trim() &&
    !option.description?.trim() &&
    !option.additionalCost &&
    !option.numberOfDays &&
    !hasLineItemValue
  );
};
